import { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Filter, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

/**
 * Sortable and filterable table header cell
 */
export const TableHeaderCell = ({
  label,
  field,
  sortable = false,
  filterable = false,
  filterOptions = [],
  currentSort,
  currentFilters = [],
  onSort,
  onFilter,
  className = ''
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSort = () => {
    if (!sortable || !onSort) return;
    
    if (currentSort?.field !== field) {
      onSort({ field, direction: 'asc' });
    } else if (currentSort.direction === 'asc') {
      onSort({ field, direction: 'desc' });
    } else {
      onSort(null); // Clear sort
    }
  };

  const handleFilterToggle = (value) => {
    if (!onFilter) return;
    
    const newFilters = currentFilters.includes(value)
      ? currentFilters.filter(f => f !== value)
      : [...currentFilters, value];
    
    onFilter(field, newFilters);
  };

  const handleClearFilter = () => {
    if (onFilter) {
      onFilter(field, []);
    }
    setFilterOpen(false);
  };

  const handleSelectAll = () => {
    if (onFilter) {
      onFilter(field, []);
    }
  };

  const isFiltered = currentFilters.length > 0;
  const isSorted = currentSort?.field === field;

  return (
    <th className={`relative ${className}`}>
      <div className="flex items-center gap-1">
        {/* Label and Sort */}
        <button
          onClick={handleSort}
          className={`flex items-center gap-1 ${sortable ? 'cursor-pointer hover:text-[#005EF8]' : ''}`}
          disabled={!sortable}
        >
          <span>{label}</span>
          {sortable && (
            <span className="text-[#808080]">
              {isSorted ? (
                currentSort.direction === 'asc' ? (
                  <ChevronUp className="w-4 h-4 text-[#005EF8]" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-[#005EF8]" />
                )
              ) : (
                <ChevronsUpDown className="w-3.5 h-3.5" />
              )}
            </span>
          )}
        </button>

        {/* Filter Button */}
        {filterable && filterOptions.length > 0 && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`p-1 rounded hover:bg-muted transition-colors ${
                isFiltered ? 'text-[#005EF8]' : 'text-[#808080]'
              }`}
              data-testid={`filter-${field}`}
            >
              <Filter className={`w-3.5 h-3.5 ${isFiltered ? 'fill-current' : ''}`} />
            </button>

            {/* Active filter indicator */}
            {isFiltered && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#005EF8] rounded-full" />
            )}

            {/* Filter Dropdown */}
            {filterOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white border rounded-lg shadow-lg min-w-[180px] py-1">
                <div className="px-3 py-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium text-[#323033]">Filter {label}</span>
                  {isFiltered && (
                    <button
                      onClick={handleClearFilter}
                      className="text-xs text-[#005EF8] hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                <div className="max-h-[240px] overflow-y-auto">
                  {/* All option */}
                  <label
                    className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer"
                    onClick={handleSelectAll}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      !isFiltered ? 'bg-[#005EF8] border-[#005EF8]' : 'border-gray-300'
                    }`}>
                      {!isFiltered && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-sm">All</span>
                  </label>
                  
                  {filterOptions.map((option) => {
                    const value = typeof option === 'object' ? option.value : option;
                    const label = typeof option === 'object' ? option.label : option;
                    const isChecked = currentFilters.includes(value);
                    
                    return (
                      <label
                        key={value}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer"
                        onClick={() => handleFilterToggle(value)}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isChecked ? 'bg-[#005EF8] border-[#005EF8]' : 'border-gray-300'
                        }`}>
                          {isChecked && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );
};

/**
 * Active filters display (compact pills instead of big chips)
 */
export const ActiveFilters = ({ filters, onClear, onClearAll }) => {
  const activeFilters = Object.entries(filters).filter(([_, values]) => values.length > 0);
  
  if (activeFilters.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap text-sm">
      <span className="text-[#5A5A5A]">Filters:</span>
      {activeFilters.map(([field, values]) => (
        <span
          key={field}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#005EF8]/10 text-[#005EF8] rounded-full text-xs"
        >
          <span className="font-medium capitalize">{field.replace(/_/g, ' ')}</span>
          <span>({values.length})</span>
          <button
            onClick={() => onClear(field)}
            className="hover:bg-[#005EF8]/20 rounded-full p-0.5"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      {activeFilters.length > 1 && (
        <button
          onClick={onClearAll}
          className="text-xs text-[#005EF8] hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
};
